/* eslint-disable react/forbid-foreign-prop-types */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Badge from '../badge';
import { IconColors, IconSizes } from '../icon';
import './button.scss';

const ButtonTypes = {
  PRIMARY: 'btn-primary',
  SECONDARY: 'btn-secondary',
  TERTIARY: 'btn-tertiary',
  LINK: 'btn-link',
  DESTRUCTIVE: 'btn-destructive',
  LINK_SECONDARY: 'btn-link-secondary',
  LINK_DESTRUCTIVE: 'btn-link-destructive',
  PRIMARY_OUTLINED: 'btn-primary outlined',
  DESTRUCTIVE_OUTLINED: 'btn-destructive outlined',
};

const ButtonAppearance = {
  DEFAULT: 'default',
  TEXT: 'text', // Appearance type text removes all background effects
};

const ButtonSizes = {
  SMALL: 'size-s',
  MEDIUM: 'size-m',
  X_SMALL: 'size-xs',
  XX_SMALL: 'size-xxs',
  XXX_SMALL: 'size-xxxs',
};

const ButtonSizeIconSizeMap = {
  [ButtonSizes.X_SMALL]: IconSizes.SMALL,
  [ButtonSizes.XX_SMALL]: IconSizes.X_SMALL,
  [ButtonSizes.XXX_SMALL]: IconSizes.X_SMALL,
  [ButtonSizes.SMALL]: IconSizes.MEDIUM,
  [ButtonSizes.MEDIUM]: IconSizes.MEDIUM,
};

const ButtonTestIds = {
  BUTTON: 'button',
  ACTION_LABEL: 'actionLabel',
};

const Button = React.forwardRef(({
  label, type, renderIcon, size, disabled, onClick,
  className, title, borderLess, appearance, loading,
  badgeProps, testIds,
}, ref) => {
  const classNames = clsx(
    'btn-cont',
    type,
    size,
    className,
    appearance,
    { 'btn-less-pad': !label },
    { 'border-less': borderLess },
  );

  const getIcon = () => {
    if (renderIcon) {
      let iconColor;

      switch (type) {
        case ButtonTypes.PRIMARY:
        case ButtonTypes.DESTRUCTIVE:
          iconColor = IconColors.LIGHT;
          break;
        case ButtonTypes.SECONDARY:
        case ButtonTypes.LINK_SECONDARY:
          if (disabled) {
            iconColor = IconColors.GREY_200;
          } else {
            iconColor = IconColors.DARK;
          }
          break;
        case ButtonTypes.LINK:
        case ButtonTypes.TERTIARY:
        case ButtonTypes.PRIMARY_OUTLINED:
          if (disabled) {
            iconColor = IconColors.PRIMARY_200;
          } else {
            iconColor = IconColors.PRIMARY;
          }
          break;
        case ButtonTypes.LINK_DESTRUCTIVE:
        case ButtonTypes.DESTRUCTIVE_OUTLINED:
          if (disabled) {
            iconColor = IconColors.ERROR_300;
          } else {
            iconColor = IconColors.ERROR;
          }
          break;

        default:
          iconColor = IconColors.DARK;
          break;
      }
      return renderIcon({ size: ButtonSizeIconSizeMap[size], color: iconColor });
    }
    return null;
  };

  const getText = () => {
    if (label) {
      return (
        <span
          className={clsx('btn-text', appearance)}
          data-testid={testIds[ButtonTestIds.ACTION_LABEL]}
        >
          {label}
        </span>
      );
    }
    return null;
  };

  const getBadge = () => (
    <Badge
      {...badgeProps}
    />
  );

  const smallLoader = label.length === 0;
  const getLoaderWithInvisibleContent = () => (
    <div className="btn-loader-cont">
      <div className="btn-hidden-content">
        {getIcon()}
        {getText()}
      </div>
      <div className={clsx('btn-loader', { 'btn-loader-small': smallLoader })}>
        <div className="btn-loader-bit" />
        <div className="btn-loader-bit" />
        <div className="btn-loader-bit" />
        <div className="btn-loader-bit" />
      </div>
    </div>
  );

  return (
    <button
      type="button"
      className={classNames}
      disabled={disabled || loading}
      onClick={onClick}
      title={title}
      ref={ref}
      data-testid={testIds[ButtonTestIds.BUTTON]}
    >
      <div className="btn-content">
        {
          loading ? getLoaderWithInvisibleContent() : (
            <>
              {getIcon()}
              {getText()}
              {badgeProps && badgeProps?.label && getBadge()}
            </>
          )
        }
      </div>
    </button>
  );
});

const ButtonProps = {
  label: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(Object.values(ButtonTypes)),
  size: PropTypes.oneOf(Object.values(ButtonSizes)),
  renderIcon: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  borderLess: PropTypes.bool,
  appearance: PropTypes.oneOf(Object.values(ButtonAppearance)),
  loading: PropTypes.bool,
  badgeProps: PropTypes.shape(Badge.propTypes),
  // add ref as a prop as well to the wrapper component
  ref: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  testIds: PropTypes.shape({}),
};

Button.propTypes = ButtonProps;

Button.defaultProps = {
  label: '',
  title: '',
  type: ButtonTypes.PRIMARY,
  size: ButtonSizes.SMALL,
  renderIcon: null,
  className: '',
  disabled: false,
  borderLess: false,
  onClick: () => { },
  appearance: ButtonAppearance.DEFAULT,
  loading: false,
  badgeProps: Badge.defaultProps,
  ref: null,
  testIds: {},
};

export {
  Button as default,
  ButtonTypes,
  ButtonSizes,
  ButtonAppearance,
  ButtonTestIds,
};
